import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert, MenuItem } from '@mui/material';
import { Add, Edit, Delete, UploadFile } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { BulkImportDialog } from '../../components/BulkImportDialog';
import { ProfileStatusChip } from '../../components/ProfileStatusChip';
import { bulkOnboardApi } from '../../api/bulkOnboard.api';
import { ClientFormDialog } from './ClientFormDialog';
import { clientsApi, type Client, type CreateClientInput } from '../../api/clients.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function ClientListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState<Client | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['clients', page, limit, search, billingCycle],
    queryFn: () => clientsApi.list({ page, limit, search: search || undefined, billingCycle: billingCycle || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['clients'] });

  const createMut = useMutation({
    mutationFn: (input: CreateClientInput) => clientsApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateClientInput }) => clientsApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => clientsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Client>[] = [
    { key: 'clientCode', label: 'Code', width: 110 },
    { key: 'clientName', label: 'Name' },
    { key: 'gstNumber', label: 'GST' },
    { key: 'contactPerson', label: 'Contact' },
    { key: 'profileStatus', label: 'Profile', render: (r) => <ProfileStatusChip status={r.profileStatus} /> },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader
        title="Client Master"
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="clients" permission="client.export" filters={{ search, billingCycle }} />
            {hasPermission('client.create') && <Button startIcon={<UploadFile />} variant="outlined" onClick={() => setBulkOpen(true)}>Bulk Upload</Button>}
            {hasPermission('client.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Client</Button>}
          </Stack>
        }
      />
      <Stack direction="row" spacing={2} mb={2}>
        <TextField size="small" placeholder="Search name / code / GST" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <TextField select size="small" label="Billing cycle" value={billingCycle} onChange={(e) => { setBillingCycle(e.target.value); setPage(1); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {['monthly', 'quarterly', 'half_yearly', 'yearly', 'milestone'].map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('client.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('client.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <ClientFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete client"
        message={`Soft-delete ${deleting?.clientName}? Blocked if active projects exist.`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload clients"
        entityLabel="Client"
        columns={[
          { header: 'Client Name', sample: 'Acme Power Pvt Ltd', required: true },
          { header: 'GST Number', sample: '27AAAAA0000A1Z5' },
          { header: 'PAN Number', sample: 'AAAAA0000A' },
          { header: 'Address', sample: '12 MG Road, Pune' },
          { header: 'Contact Person', sample: 'Ravi Kumar' },
          { header: 'Email', sample: 'ravi@acme.com' },
          { header: 'Mobile', sample: '9876543210' },
          { header: 'State', sample: 'Maharashtra' },
        ]}
        onImport={(rows, createLogins) => bulkOnboardApi.clients(rows, createLogins)}
        onDone={invalidate}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
